import React, { useState, useEffect, useCallback, useRef } from 'react';
import usePageTitle from '@/hooks/usePageTitle';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { HelpCircle, Check, ChevronRight, AlertCircle, Ban, ArrowLeft, Send, Maximize, Minimize, Edit3, X, ListChecks, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function JudgeScoringPage({ apparatus }) {
  // Page title
  usePageTitle('Judge Scoring');

  // Auth state
  const [pinEntered, setPinEntered] = useState(false);
  const [pin, setPin] = useState('');
  const [competitionId, setCompetitionId] = useState('');
  const [panel, setPanel] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pendingFullscreen, setPendingFullscreen] = useState(false);

  // For /mag: the apparatus is derived from the rotation schedule (round/rotation)
  // For all other scoring pages: matches the prop directly.
  const isMag = apparatus === 'mag';
  const [magResolvedApparatus, setMagResolvedApparatus] = useState('');
  const [magActiveApparatuses, setMagActiveApparatuses] = useState([]); // when >1 MAG apparatus in same rotation
  const effectiveApparatus = isMag ? magResolvedApparatus : apparatus;
  
  // Rotation state
  const [roundNumber, setRoundNumber] = useState(1);
  const [rotationNumber, setRotationNumber] = useState(1);
  // Programme-derived structure — populated once competitionId is known
  const [availableRounds, setAvailableRounds] = useState([]);
  const [rotationsByRound, setRotationsByRound] = useState({});
  const [gymnasts, setGymnasts] = useState([]);
  const [scoredGymnastIds, setScoredGymnastIds] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);

  // Finished Rotation state
  const [rotationMarkedFinished, setRotationMarkedFinished] = useState(false);
  
  // Selected gymnast for scoring
  const [selectedGymnast, setSelectedGymnast] = useState(null);
  const [showSplash, setShowSplash] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Judge config (dJudges / eJudges for this apparatus+panel, loaded after PIN verify)
  const [judgeConfig, setJudgeConfig] = useState({ dJudges: 1, eJudges: 1 });
  // Scoring lock state — polled from open-state while a session is active
  const [scoringLocked, setScoringLocked] = useState(false);

  // Current gymnast score
  const [currentScore, setCurrentScore] = useState({
    difficulty: '',
    execution: '',   // single-E value or computed E AVG for multi-E
    execution1: '', execution2: '', execution3: '', execution4: '', execution5: '',
    bonus: '0',
    neutralDeductions: '0',
    didNotCompete: false
  });

  // Vault double-attempt state — active when the selected gymnast's category
  // has vaultCount === 2 on the vault apparatus. Judges score Vault 1 and
  // Vault 2 independently; each is submitted separately with attemptNumber.
  const [vaultAttempt, setVaultAttempt] = useState(1);
  // Set of composite ids `${gymnastId}|${attempt}` for scored vault attempts.
  const [scoredVaultAttempts, setScoredVaultAttempts] = useState(new Set());
  // Map gid → {1: scoreRecord, 2: scoreRecord} used to prefill on switch.
  const [existingScoresByAttempt, setExistingScoresByAttempt] = useState({});
  
  // Active field for on-screen keypad
  const [activeScoringField, setActiveScoringField] = useState('d'); // 'd', 'e', 'nd'
  
  // Competition info
  const [competition, setCompetition] = useState(null);
  
  // Correction dialog state
  const [showCorrectionDialog, setShowCorrectionDialog] = useState(false);
  const [correctionNumber, setCorrectionNumber] = useState('');
  const [correctionLetter, setCorrectionLetter] = useState('');
  const [correctionD, setCorrectionD] = useState('');
  const [correctionE, setCorrectionE] = useState('');
  const [correctionB, setCorrectionB] = useState('0');
  const [correctionND, setCorrectionND] = useState('0');
  const [correctionSubmitting, setCorrectionSubmitting] = useState(false);
  const [showCorrectionConfirm, setShowCorrectionConfirm] = useState(false);
  const [activeField, setActiveField] = useState('number'); // 'number', 'd', 'e', 'b', 'nd'

  // Submitted-scores viewer state (judge-facing)
  const [showSubmitted, setShowSubmitted] = useState(false);
  const [submittedScores, setSubmittedScores] = useState([]);
  const [submittedLoading, setSubmittedLoading] = useState(false);

  // Training video synchronized playback state
  const [showVideo, setShowVideo] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoPlayAt, setVideoPlayAt] = useState(null);
  const [videoCountdown, setVideoCountdown] = useState(null);
  const [videoReady, setVideoReady] = useState(false);
  const [videoError, setVideoError] = useState(null);
  const [videoIsPlaying, setVideoIsPlaying] = useState(false);
  const videoRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const playTimeoutRef = useRef(null);

  // Store adjusted play time for the video play effect
  const [adjustedPlayAtMs, setAdjustedPlayAtMs] = useState(null);

  // Poll for training video broadcast (every 2 seconds when logged in)
  useEffect(() => {
    if (!pinEntered || !competitionId) return;
    
    const checkTrainingVideo = async () => {
      try {
        const response = await axios.get(`${API}/training-video/status/${competitionId}`);
        const { isPlaying, videoUrl: vUrl, playAt, serverTime } = response.data;
        
        if (isPlaying && vUrl && playAt) {
          // Ensure full URL for video
          const fullVideoUrl = vUrl.startsWith('/api') 
            ? `${BACKEND_URL}${vUrl}` 
            : vUrl;
          
          // Only set up if this is a new broadcast (different playAt time)
          if (playAt !== videoPlayAt) {
            console.log('[Video Sync] New broadcast detected');
            console.log('[Video Sync] Video URL:', fullVideoUrl);
            console.log('[Video Sync] Play at:', playAt);
            
            // Calculate delay until play time
            const serverTimeMs = new Date(serverTime).getTime();
            const clientTimeMs = Date.now();
            const clockOffset = serverTimeMs - clientTimeMs;
            const playAtMs = new Date(playAt).getTime();
            const computedAdjustedPlayAtMs = playAtMs - clockOffset;
            const delayMs = computedAdjustedPlayAtMs - Date.now();
            
            console.log('[Video Sync] Delay until play:', delayMs, 'ms');
            console.log('[Video Sync] Clock offset:', clockOffset, 'ms');
            
            // If playAt is too far in the past, skip
            if (delayMs < -30000) {
              console.log('[Video Sync] Broadcast too old, skipping');
              return;
            }
            
            // Clear any existing timers
            if (countdownIntervalRef.current) {
              clearInterval(countdownIntervalRef.current);
              countdownIntervalRef.current = null;
            }
            if (playTimeoutRef.current) {
              clearTimeout(playTimeoutRef.current);
              playTimeoutRef.current = null;
            }
            
            // Reset states for new broadcast
            setVideoIsPlaying(false);
            setVideoUrl(fullVideoUrl);
            setVideoPlayAt(playAt);
            setVideoReady(false);
            setAdjustedPlayAtMs(computedAdjustedPlayAtMs);
            setVideoCountdown(Math.max(0, Math.ceil(delayMs / 1000)));
            setShowVideo(true);
            
            // Start countdown interval - store the target time in a closure
            const targetTime = computedAdjustedPlayAtMs;
            countdownIntervalRef.current = setInterval(() => {
              const now = Date.now();
              const remaining = Math.ceil((targetTime - now) / 1000);
              console.log('[Video Sync] Countdown:', remaining);
              
              if (remaining <= 0) {
                setVideoCountdown(0);
                clearInterval(countdownIntervalRef.current);
                countdownIntervalRef.current = null;
              } else {
                setVideoCountdown(remaining);
              }
            }, 500);
          }
        } else if (!isPlaying && showVideo) {
          // Admin stopped the video
          console.log('[Video Sync] Admin stopped video');
          
          if (videoRef.current) {
            videoRef.current.pause();
          }
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          if (playTimeoutRef.current) {
            clearTimeout(playTimeoutRef.current);
            playTimeoutRef.current = null;
          }
          
          setShowVideo(false);
          setVideoUrl(null);
          setVideoPlayAt(null);
          setVideoCountdown(null);
          setVideoReady(false);
        }
      } catch (error) {
        console.error('[Video Sync] Poll error:', error);
      }
    };
    
    checkTrainingVideo();
    const interval = setInterval(checkTrainingVideo, 2000);
    
    return () => {
      clearInterval(interval);
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      if (playTimeoutRef.current) {
        clearTimeout(playTimeoutRef.current);
      }
    };
  }, [pinEntered, competitionId, videoPlayAt, showVideo]);

  // Handle training video end
  const handleTrainingVideoEnd = async () => {
    console.log('[Video Sync] Video ended');
    
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    if (playTimeoutRef.current) {
      clearTimeout(playTimeoutRef.current);
      playTimeoutRef.current = null;
    }
    
    setShowVideo(false);
    setVideoUrl(null);
    setVideoPlayAt(null);
    setVideoCountdown(null);
    setVideoReady(false);
    setVideoError(null);
    setVideoIsPlaying(false);
    setAdjustedPlayAtMs(null);
    
    try {
      await axios.post(`${API}/training-video/finished/${competitionId}`);
    } catch (error) {
      // Ignore
    }
  };
  
  // Effect to trigger video play when video is ready and play time arrives
  useEffect(() => {
    if (!showVideo || !videoReady || !adjustedPlayAtMs) return;
    
    const schedulePlay = () => {
      const now = Date.now();
      const delayMs = adjustedPlayAtMs - now;
      
      // Clear any existing timeout
      if (playTimeoutRef.current) {
        clearTimeout(playTimeoutRef.current);
        playTimeoutRef.current = null;
      }
      
      const startVideo = () => {
        console.log('[Video Sync] Starting video playback...');
        if (videoRef.current) {
          videoRef.current.currentTime = 0;
          videoRef.current.play()
            .then(() => {
              console.log('[Video Sync] Video started playing!');
              setVideoIsPlaying(true);
              setVideoCountdown(null); // Clear countdown when playing
            })
            .catch(err => {
              console.error('[Video Sync] Play failed:', err);
              // Retry once after a short delay
              setTimeout(() => {
                if (videoRef.current) {
                  videoRef.current.play()
                    .then(() => {
                      setVideoIsPlaying(true);
                      setVideoCountdown(null);
                    })
                    .catch(e => console.error('[Video Sync] Retry play failed:', e));
                }
              }, 300);
            });
        }
      };
      
      if (delayMs <= 0) {
        // Play immediately
        console.log('[Video Sync] Video ready and play time reached - playing now!');
        startVideo();
      } else {
        // Schedule play for exact time
        console.log('[Video Sync] Video ready - scheduling play in', delayMs, 'ms');
        playTimeoutRef.current = setTimeout(() => {
          console.log('[Video Sync] Play time reached! Playing...');
          startVideo();
        }, delayMs);
      }
    };
    
    schedulePlay();
    
    return () => {
      if (playTimeoutRef.current) {
        clearTimeout(playTimeoutRef.current);
        playTimeoutRef.current = null;
      }
    };
  }, [showVideo, videoReady, adjustedPlayAtMs]);
  
  // Refresh signal state
  const [lastRefreshTimestamp, setLastRefreshTimestamp] = useState(null);

  // Session storage key for this apparatus
  const sessionKey = `judge_session_${apparatus}`;

  // Restore session on mount (for admin-triggered refresh)
  useEffect(() => {
    const savedSession = localStorage.getItem(sessionKey);
    if (savedSession) {
      try {
        const session = JSON.parse(savedSession);
        // Check if session is recent (within last 5 minutes)
        const sessionAge = Date.now() - (session.savedAt || 0);
        if (sessionAge < 5 * 60 * 1000) {
          console.log('[Judge Session] Restoring session after refresh:', session);
          setPinEntered(true);
          setCompetitionId(session.competitionId || '');
          setPanel(session.panel || '');
          setRoundNumber(session.roundNumber || 1);
          setRotationNumber(session.rotationNumber || 1);
          
          // Restore fullscreen silently on first user interaction (browser requires a gesture)
          if (session.wasFullscreen) {
            setPendingFullscreen(true);
          }
          
          // Clear the saved session after restore
          localStorage.removeItem(sessionKey);
        } else {
          // Session too old, clear it
          localStorage.removeItem(sessionKey);
        }
      } catch (e) {
        console.error('[Judge Session] Failed to restore session:', e);
        localStorage.removeItem(sessionKey);
      }
    }
  }, [sessionKey]);

  // Fetch competition data when session is restored or after PIN entry
  useEffect(() => {
    if (pinEntered && competitionId && !competition) {
      const fetchCompetition = async () => {
        try {
          const compResponse = await axios.get(`${API}/public/competitions`);
          const competitions = compResponse.data.competitions || compResponse.data || [];
          const comp = competitions.find(c => c.id === competitionId);
          if (comp) {
            setCompetition(comp);
            console.log('[Judge Session] Competition data loaded:', comp.title);
          }
        } catch (err) {
          console.error('[Judge Session] Error fetching competition:', err);
        }
      };
      fetchCompetition();
    }
  }, [pinEntered, competitionId, competition]);

  // Normalise panel string so "SINGLE PANEL" and "single" are treated the same
  // but verify-code returns panel "single" from the DB. Normalise both sides so they match.
  const normalizePanel = (p) => {
    if (!p) return 'single';
    const u = p.trim().toUpperCase();
    if (u === 'SINGLE' || u === 'SINGLE PANEL') return 'single';
    return u;
  };

  // Load judge config for this apparatus+panel after PIN verify
  const loadJudgeConfig = useCallback(async (compId, app, pnl) => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/scoring/judge-config/${compId}`);
      const configs = res.data.configs || [];
      const normPnl = normalizePanel(pnl || 'single');
      const match = configs.find(c =>
        c.apparatus === (app || '').toLowerCase() &&
        normalizePanel(c.panel) === normPnl
      );
      if (match) setJudgeConfig({ dJudges: match.dJudges || 1, eJudges: match.eJudges || 1 });
    } catch { /* use defaults */ }
  }, []); // eslint-disable-line

  // Reload judge config after session restore, panel or apparatus change
  // (PIN-verify also calls it directly, this catches the refresh case)
  useEffect(() => {
    if (!pinEntered || !competitionId) return;
    const app = isMag ? magResolvedApparatus : apparatus;
    if (!app) return;
    loadJudgeConfig(competitionId, app, panel);
  }, [pinEntered, competitionId, panel, apparatus, magResolvedApparatus, isMag, loadJudgeConfig]);

  // Numeric keypad handler for scoring
  const handleScoringKeypadPress = (key) => {
    const updateField = (field, currentValue, maxLength = 6) => {
      let newValue = currentValue;
      if (key === 'C') {
        newValue = '';
      } else if (key === '⌫') {
        newValue = currentValue.slice(0, -1);
      } else if (key === '.' && currentValue.includes('.')) {
        return currentValue; // Don't add another decimal
      } else if (currentValue.length < maxLength) {
        newValue = currentValue + key;
      }
      return newValue;
    };

    // e1–e5: update that field, then recompute E AVG
    if (/^e[1-5]$/.test(activeScoringField)) {
      const idx = parseInt(activeScoringField[1]);
      const field = `execution${idx}`;
      setCurrentScore(prev => {
        const updated = { ...prev, [field]: updateField(field, prev[field] || '') };
        // Recompute E AVG from all filled E fields
        const eCount = judgeConfig.eJudges || 1;
        const vals = [];
        for (let i = 1; i <= eCount; i++) {
          const v = parseFloat(updated[`execution${i}`]);
          if (!isNaN(v)) vals.push(v);
        }
        updated.execution = vals.length === eCount
          ? (vals.reduce((a, b) => a + b, 0) / eCount).toFixed(3)
          : '';
        return updated;
      });
      return;
    }

    switch (activeScoringField) {
      case 'd':
        setCurrentScore(prev => ({ ...prev, difficulty: updateField('difficulty', prev.difficulty) }));
        break;
      case 'e':
        setCurrentScore(prev => ({ ...prev, execution: updateField('execution', prev.execution) }));
        break;
      case 'b':
        setCurrentScore(prev => ({ ...prev, bonus: updateField('bonus', prev.bonus || '') }));
        break;
      case 'nd':
        setCurrentScore(prev => ({ ...prev, neutralDeductions: updateField('neutralDeductions', prev.neutralDeductions) }));
        break;
    }
  };

  // Numeric keypad handler
  const handleKeypadPress = (key) => {
    const updateField = (setter, currentValue, maxLength = 10) => {
      if (key === 'C') {
        setter('');
      } else if (key === '⌫') {
        setter(currentValue.slice(0, -1));
      } else if (key === '.' && currentValue.includes('.')) {
        // Don't add another decimal
        return;
      } else if (currentValue.length < maxLength) {
        setter(currentValue + key);
      }
    };

    switch (activeField) {
      case 'number':
        if (key === '.' || key === 'C' || key === '⌫') {
          updateField(setCorrectionNumber, correctionNumber, 4);
        } else {
          updateField(setCorrectionNumber, correctionNumber, 4);
        }
        break;
      case 'letter':
        // Letters handled separately
        break;
      case 'd':
        updateField(setCorrectionD, correctionD, 6);
        break;
      case 'e':
        updateField(setCorrectionE, correctionE, 6);
        break;
      case 'b':
        updateField(setCorrectionB, correctionB || '', 6);
        break;
      case 'nd':
        updateField(setCorrectionND, correctionND, 5);
        break;
    }
  };

  const apparatusNames = {
    vault: 'Vault',
    wagvault: 'Vault',
    magvault: 'Vault',
    bars: 'Uneven Bars',
    beam: 'Balance Beam',
    floor: 'Floor',
    wagfloor: 'Floor',
    magfloor: 'Floor',
    range: 'Range & Conditioning',
    // MAG apparatus (used by /mag)
    rings: 'Rings',
    pommel: 'Pommel Horse',
    pbars: 'Parallel Bars',
    hbar: 'High Bar',
    mag: 'MAG'
  };
  const headerApparatusLabel = isMag
    ? (magResolvedApparatus ? `MAG · ${apparatusNames[magResolvedApparatus]}` : 'MAG')
    : (apparatusNames[apparatus] || apparatus);

  // Compute E AVG from the multi-E fields and update execution
  const computeEAvg = useCallback((score, eCount) => {
    const vals = [];
    for (let i = 1; i <= eCount; i++) {
      const v = parseFloat(score[`execution${i}`]);
      if (!isNaN(v)) vals.push(v);
    }
    if (vals.length === 0) return '';
    return (vals.reduce((a, b) => a + b, 0) / eCount).toFixed(3);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch(err => {
        toast.error('Could not enter fullscreen');
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      });
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      const inFS = !!document.fullscreenElement;
      setIsFullscreen(inFS);
      // If judge accidentally exits fullscreen during an active session, prompt to return
      if (!inFS && pinEntered) {
        setPendingFullscreen(true);
      }
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, [pinEntered]);

  // Poll open-state every 10 s once PIN is entered to detect scoring lock
  useEffect(() => {
    if (!pinEntered) return;
    const checkLock = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/open-state`);
        setScoringLocked(res.data?.scoringLocked || false);
      } catch { /* silent */ }
    };
    checkLock();
    const id = setInterval(checkLock, 10000);
    return () => clearInterval(id);
  }, [pinEntered]);

  // Poll for refresh signal from SuperAdmin (every 5 seconds when logged in)
  useEffect(() => {
    if (!pinEntered) return;
    
    const checkRefreshSignal = async () => {
      try {
        const response = await axios.get(`${API}/judge/refresh-signal`);
        const serverTimestamp = response.data.timestamp;
        
        if (serverTimestamp && lastRefreshTimestamp && serverTimestamp !== lastRefreshTimestamp) {
          // New refresh signal - save state and reload the page
          toast.info('Refreshing page by admin request...');
          
          // Save current session state to localStorage before refresh
          const sessionState = {
            competitionId,
            panel,
            roundNumber,
            rotationNumber,
            wasFullscreen: !!document.fullscreenElement,
            savedAt: Date.now()
          };
          localStorage.setItem(sessionKey, JSON.stringify(sessionState));
          console.log('[Judge Session] Saved session before refresh:', sessionState);
          
          // Hard refresh — use location.reload() to avoid URL routing issues
          setTimeout(() => {
            window.location.reload();
          }, 500);
        }
        
        // Update our last known timestamp
        if (serverTimestamp && !lastRefreshTimestamp) {
          setLastRefreshTimestamp(serverTimestamp);
        } else if (serverTimestamp) {
          setLastRefreshTimestamp(serverTimestamp);
        }
      } catch (error) {
        // Silently ignore polling errors
      }
    };
    
    // Check immediately to get initial timestamp
    checkRefreshSignal();
    
    // Then poll every 5 seconds
    const interval = setInterval(checkRefreshSignal, 5000);
    return () => clearInterval(interval);
  }, [pinEntered, lastRefreshTimestamp, competitionId, panel, roundNumber, rotationNumber, sessionKey]);

  // Save session state on manual browser refresh (beforeunload)
  // This ensures the judge can resume after a manual refresh, not just admin-triggered ones.
  useEffect(() => {
    if (!pinEntered) return;
    const handleBeforeUnload = () => {
      const sessionState = {
        competitionId,
        panel,
        roundNumber,
        rotationNumber,
        wasFullscreen: !!document.fullscreenElement,
        savedAt: Date.now()
      };
      localStorage.setItem(sessionKey, JSON.stringify(sessionState));
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [pinEntered, competitionId, panel, roundNumber, rotationNumber, sessionKey]);

  // Calculate final score using competition scoring config
  const calculateFinalScore = () => {
    if (currentScore.didNotCompete) return 'DNC';
    const components = competition?.scoreComponents || { D: true, E: true, B: false, ND: true };
    const base = parseFloat(competition?.scoreBaseValue ?? 10);
    let score = base;
    if (components.D) score += (parseFloat(currentScore.difficulty) || 0);
    if (components.E) score -= (parseFloat(currentScore.execution) || 0);
    if (components.B) score += (parseFloat(currentScore.bonus) || 0);
    if (components.ND) score -= (parseFloat(currentScore.neutralDeductions) || 0);
    return score.toFixed(3);
  };

  // Check if score is valid for submission
  const canSubmit = () => {
    if (currentScore.didNotCompete) return true;
    const components = competition?.scoreComponents || { D: true, E: true, B: false, ND: true };
    if (components.D && currentScore.difficulty === '') return false;
    if (components.E) {
      const eCount = judgeConfig.eJudges || 1;
      if (eCount > 1) {
        // All E fields must be filled
        for (let i = 1; i <= eCount; i++) {
          if ((currentScore[`execution${i}`] || '') === '') return false;
        }
      } else {
        if (currentScore.execution === '') return false;
      }
    }
    return true;
  };

  // PIN verification
  const handlePinSubmit = async (e) => {
    e.preventDefault();
    
    if (pin.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    try {
      const response = await axios.post(`${API}/scoring/verify-code`, { 
        code: pin,
        apparatus: apparatus 
      });
      
      if (response.data.valid) {
        const cId = response.data.competitionId;
        const pnl = response.data.panel;
        setCompetitionId(cId);
        setPanel(pnl);
        setPinEntered(true);
        // Load judge config for this apparatus+panel
        loadJudgeConfig(cId, apparatus, pnl);
        // Button click IS a user gesture — enter fullscreen straight away
        document.documentElement.requestFullscreen().catch(() => {});
        
        try {
          const compResponse = await axios.get(`${API}/public/competitions`);
          const competitions = compResponse.data.competitions || compResponse.data || [];
          const comp = competitions.find(c => c.id === response.data.competitionId);
          setCompetition(comp);
        } catch (err) {
          console.error('Error fetching competition:', err);
        }
        
        toast.success(`Welcome to ${apparatusNames[apparatus] || apparatus} Scoring`);
      }
    } catch (error) {
      toast.error('Invalid judge code');
      setPin('');
    }
  };

  // Fetch available rounds + rotations from programme when competition is known
  useEffect(() => {
    if (!competitionId) return;
    axios.get(`${API}/competition-structure/${competitionId}`)
      .then(res => {
        const rounds = res.data.rounds || [];
        const rotByRound = res.data.rotationsByRound || {};
        setAvailableRounds(rounds);
        setRotationsByRound(rotByRound);
        // Correct current round if it doesn't exist in the programme
        if (rounds.length > 0 && !rounds.includes(roundNumber)) {
          setRoundNumber(rounds[0]);
        }
      })
      .catch(() => {/* use defaults, dropdowns will fall back to [1..5] */});
  }, [competitionId]); // eslint-disable-line

  // When roundNumber changes, ensure rotationNumber is valid for that round
  useEffect(() => {
    if (!competitionId || Object.keys(rotationsByRound).length === 0) return;
    const maxRot = rotationsByRound[roundNumber] || 0;
    if (maxRot > 0 && rotationNumber > maxRot) setRotationNumber(1);
  }, [roundNumber, rotationsByRound]); // eslint-disable-line

  // Fetch gymnasts for current rotation
  const fetchRotationGymnasts = useCallback(async () => {
    if (!competitionId) return;
    // In MAG mode we may still be resolving the apparatus
    if (isMag && !effectiveApparatus) {
      setGymnasts([]);
      setGroups([]);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/scoring/rotation-gymnasts`, {
        params: {
          competitionId,
          roundNumber,
          rotationNumber,
          apparatus: effectiveApparatus,
          panel: panel || undefined
        }
      });
      
      setGymnasts(response.data.gymnasts || []);
      setGroups(response.data.groups || []);
      
      const scoresResponse = await axios.get(`${API}/scoring/rotation-scores`, {
        params: {
          competitionId,
          apparatus: effectiveApparatus,
          roundNumber,
          rotationNumber
        }
      });
      
      const existingScores = scoresResponse.data.scores || {};
      const existingByAttempt = scoresResponse.data.scoresByAttempt || {};
      setExistingScoresByAttempt(existingByAttempt);

      // Determine which gymnasts are considered "scored" for the tile view.
      // For non-vault or 1-vault categories: any submitted score marks them done.
      // For vault + vaultCount 2 gymnasts: need BOTH attempts submitted.
      const isVaultApp = /vault/i.test(effectiveApparatus || '');
      const doneIds = [];
      const attemptSet = new Set();
      const submittedGymnastIds = Object.keys(existingScores).filter(gid =>
        existingScores[gid].difficulty !== undefined || existingScores[gid].didNotCompete
      );
      if (isVaultApp) {
        // Build attempt set
        Object.entries(existingByAttempt).forEach(([gid, byA]) => {
          Object.keys(byA).forEach(a => attemptSet.add(`${gid}|${a}`));
        });
        (response.data.gymnasts || []).forEach(g => {
          const need = Number(g.vaultCount) === 2 ? 2 : 1;
          const gotBoth = need === 2
            ? attemptSet.has(`${g.gymnastId}|1`) && attemptSet.has(`${g.gymnastId}|2`)
            : (submittedGymnastIds.includes(g.gymnastId) || attemptSet.has(`${g.gymnastId}|1`));
          if (gotBoth) doneIds.push(g.gymnastId);
        });
      } else {
        submittedGymnastIds.forEach(gid => doneIds.push(gid));
      }
      setScoredGymnastIds(doneIds);
      setScoredVaultAttempts(attemptSet);
      
    } catch (error) {
      console.error('Error fetching rotation gymnasts:', error);
      if (error.response?.status === 404) {
        setGymnasts([]);
        setGroups([]);
      }
    } finally {
      setLoading(false);
    }
  }, [competitionId, roundNumber, rotationNumber, effectiveApparatus, panel, isMag]);

  useEffect(() => {
    if (pinEntered && competitionId) {
      fetchRotationGymnasts();
    }
  }, [pinEntered, competitionId, roundNumber, rotationNumber, fetchRotationGymnasts]);

  // MAG-mode: derive apparatus from rotation schedule whenever round/rotation changes
  useEffect(() => {
    if (!isMag || !pinEntered || !competitionId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/scoring/mag-context`, {
          params: { competitionId, roundNumber, rotationNumber }
        });
        if (cancelled) return;
        const groups = res.data?.groups || [];
        const MAG = new Set(['floor','vault','rings','pommel','pbars','hbar']);
        const active = [];
        groups.forEach(g => { if (MAG.has(g.apparatus) && !active.includes(g.apparatus)) active.push(g.apparatus); });
        setMagActiveApparatuses(active);
        // Auto-select if only one MAG apparatus is active; otherwise wait for judge tap
        if (active.length === 1) setMagResolvedApparatus(active[0]);
        else if (active.length === 0) setMagResolvedApparatus('');
        else if (!active.includes(magResolvedApparatus)) setMagResolvedApparatus(''); // force pick
      } catch {
        if (!cancelled) { setMagActiveApparatuses([]); setMagResolvedApparatus(''); }
      }
    })();
    return () => { cancelled = true; };
  }, [isMag, pinEntered, competitionId, roundNumber, rotationNumber, magResolvedApparatus]);

  // Select a gymnast to score
  const handleSelectGymnast = (gymnast) => {
    setSelectedGymnast(gymnast);
    // Double-vault: default to the first attempt still needing a score.
    const isVaultApp = /vault/i.test(effectiveApparatus || '');
    const isDouble = isVaultApp && Number(gymnast?.vaultCount) === 2;
    let initialAttempt = 1;
    if (isDouble) {
      const has1 = scoredVaultAttempts.has(`${gymnast.gymnastId}|1`);
      const has2 = scoredVaultAttempts.has(`${gymnast.gymnastId}|2`);
      initialAttempt = has1 && !has2 ? 2 : 1;
    }
    setVaultAttempt(initialAttempt);
    // Pre-fill from previously submitted attempt (allows correcting)
    const prev = (existingScoresByAttempt?.[gymnast.gymnastId] || {})[initialAttempt];
    if (prev) {
      setCurrentScore({
        difficulty: prev.difficulty || '',
        execution: prev.execution || '',
        execution1: prev.execution1 || '', execution2: prev.execution2 || '',
        execution3: prev.execution3 || '', execution4: prev.execution4 || '', execution5: prev.execution5 || '',
        bonus: prev.bonus || '0',
        neutralDeductions: prev.neutralDeductions || '0',
        didNotCompete: !!prev.didNotCompete,
      });
    } else {
      setCurrentScore({
        difficulty: '',
        execution: '',
        execution1: '', execution2: '', execution3: '', execution4: '', execution5: '',
        bonus: '0',
        neutralDeductions: '0',
        didNotCompete: false
      });
    }
    setActiveScoringField('d'); // Start with difficulty field
    setShowSplash(true);
    
    setTimeout(() => {
      setShowSplash(false);
    }, 2500);
  };

  // Switch between Vault 1 and Vault 2 while scoring the same gymnast.
  const switchVaultAttempt = (attempt) => {
    if (!selectedGymnast) return;
    setVaultAttempt(attempt);
    const prev = (existingScoresByAttempt?.[selectedGymnast.gymnastId] || {})[attempt];
    if (prev) {
      setCurrentScore({
        difficulty: prev.difficulty || '',
        execution: prev.execution || '',
        execution1: prev.execution1 || '', execution2: prev.execution2 || '',
        execution3: prev.execution3 || '', execution4: prev.execution4 || '', execution5: prev.execution5 || '',
        bonus: prev.bonus || '0',
        neutralDeductions: prev.neutralDeductions || '0',
        didNotCompete: !!prev.didNotCompete,
      });
    } else {
      setCurrentScore({ difficulty: '', execution: '', execution1: '', execution2: '', execution3: '', execution4: '', execution5: '', bonus: '0', neutralDeductions: '0', didNotCompete: false });
    }
    setActiveScoringField('d');
  };

  // Mark as Did Not Compete
  const markDidNotCompete = () => {
    setCurrentScore({
      difficulty: '0', execution: '0',
      execution1: '', execution2: '', execution3: '', execution4: '', execution5: '',
      bonus: '0', neutralDeductions: '0', didNotCompete: true
    });
  };

  // Submit score for current gymnast
  const handleSubmitScore = async () => {
    if (!canSubmit()) {
      toast.error('Please enter D and E scores or mark as DNC');
      return;
    }

    const isVaultApp = /vault/i.test(effectiveApparatus || '');
    const isDoubleVault = isVaultApp && Number(selectedGymnast?.vaultCount) === 2;
    const attempt = isDoubleVault ? vaultAttempt : 1;

    setSubmitting(true);
    try {
      await axios.post(`${API}/scoring/submit-rotation`, {
        competitionId,
        apparatus: effectiveApparatus,
        panel,
        roundNumber,
        rotationNumber,
        scores: [{
          gymnastId: selectedGymnast.gymnastId,
          gymnastNumber: `${selectedGymnast.number}${selectedGymnast.letter || ''}`,
          difficulty: currentScore.difficulty || '0',
          execution: currentScore.execution || '0',
          bonus: currentScore.bonus || '0',
          neutralDeductions: currentScore.neutralDeductions || '0',
          didNotCompete: currentScore.didNotCompete,
          attemptNumber: attempt,
        }]
      });

      toast.success(`Score submitted for ${selectedGymnast.name}${isDoubleVault ? ` (Vault ${attempt})` : ''}`);

      // Track submitted attempt
      const nextAttempts = new Set(scoredVaultAttempts);
      nextAttempts.add(`${selectedGymnast.gymnastId}|${attempt}`);
      setScoredVaultAttempts(nextAttempts);

      // For double vault, if attempt 1 just landed, switch to attempt 2
      // automatically instead of jumping back to the tile list.
      if (isDoubleVault && attempt === 1 && !nextAttempts.has(`${selectedGymnast.gymnastId}|2`)) {
        setVaultAttempt(2);
        setCurrentScore({ difficulty: '', execution: '', bonus: '0', neutralDeductions: '0', didNotCompete: false });
        setActiveScoringField('d');
        setSubmitting(false);
        return;
      }

      // Otherwise mark the gymnast tile complete
      const need = isDoubleVault ? 2 : 1;
      const gotAll = need === 2
        ? nextAttempts.has(`${selectedGymnast.gymnastId}|1`) && nextAttempts.has(`${selectedGymnast.gymnastId}|2`)
        : true;
      if (gotAll) {
        const newScoredIds = [...scoredGymnastIds, selectedGymnast.gymnastId];
        setScoredGymnastIds(newScoredIds);
        const unscoredCount = gymnasts.filter(g => !newScoredIds.includes(g.gymnastId)).length;
        if (unscoredCount === 0) {
          toast.success(`Rotation ${rotationNumber} complete!`);
          // Always mark as finished in the system when all scores are in
          axios.post(`${API}/rotation-status/finish`, {
            competitionId,
            roundNumber,
            rotationNumber,
            apparatus: effectiveApparatus,
            panel: panel || 'single'
          }).catch(() => {});
          setRotationMarkedFinished(true);
          // Only auto-advance if judge hasn't already pressed the Finished Rotation button
          if (!rotationMarkedFinished) {
            if (rotationNumber < 5) {
              setRotationNumber(prev => prev + 1);
              setScoredGymnastIds([]);
              setScoredVaultAttempts(new Set());
            } else {
              toast.success('All rotations complete for this round!');
            }
          }
        }
      }

      setSelectedGymnast(null);
      setVaultAttempt(1);

    } catch (error) {
      console.error('Error submitting score:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit score');
    } finally {
      setSubmitting(false);
    }
  };

  // Go back to tiles without submitting
  const handleBack = () => {
    setSelectedGymnast(null);
    setShowSplash(false);
  };

  // Reset finished state when rotation/round changes
  useEffect(() => {
    setRotationMarkedFinished(false);
  }, [competitionId, roundNumber, rotationNumber]);

  // Toggle rotation finished (green tick button)
  const markRotationFinished = async () => {
    const payload = {
      competitionId,
      roundNumber,
      rotationNumber,
      apparatus: effectiveApparatus,
      panel: panel || 'single'
    };
    try {
      if (rotationMarkedFinished) {
        await axios.post(`${API}/rotation-status/unfinish`, payload);
        setRotationMarkedFinished(false);
        toast.info('Rotation unmarked');
      } else {
        await axios.post(`${API}/rotation-status/finish`, payload);
        setRotationMarkedFinished(true);
        toast.success('Rotation marked as complete');
      }
    } catch (e) {
      console.error('Failed to toggle rotation status:', e);
      toast.error('Failed to update rotation status');
    }
  };

  // Help request
  const handleHelpRequest = async () => {
    try {
      await axios.post(`${API}/help-alerts`, {
        competitionId,
        apparatus: effectiveApparatus,
        panel
      });
      toast.success('Help request sent to control desk');
    } catch (error) {
      toast.error('Failed to send help request');
    }
  };

  // Fetch this panel's submitted scores
  const fetchSubmittedScores = useCallback(async () => {
    if (!competitionId || !effectiveApparatus) return;
    setSubmittedLoading(true);
    try {
      const response = await axios.get(`${API}/scoring/panel-scores`, {
        params: {
          competition_id: competitionId,
          apparatus: effectiveApparatus,
          panel: panel || 'single',
          limit: 100,
        }
      });
      setSubmittedScores(response.data.scores || []);
    } catch (error) {
      toast.error('Failed to load submitted scores');
    } finally {
      setSubmittedLoading(false);
    }
  }, [competitionId, effectiveApparatus, panel]);

  const openSubmittedDrawer = () => {
    setShowSubmitted(true);
    fetchSubmittedScores();
  };

  // Correction functions
  const openCorrectionDialog = () => {
    setCorrectionNumber('');
    setCorrectionLetter('');
    setCorrectionD('');
    setCorrectionE('');
    setCorrectionND('0');
    setShowCorrectionConfirm(false);
    setShowCorrectionDialog(true);
  };

  const calculateCorrectionFinal = () => {
    const components = competition?.scoreComponents || { D: true, E: true, B: false, ND: true };
    const base = parseFloat(competition?.scoreBaseValue ?? 10);
    let score = base;
    if (components.D) score += (parseFloat(correctionD) || 0);
    if (components.E) score -= (parseFloat(correctionE) || 0);
    if (components.B) score += (parseFloat(correctionB) || 0);
    if (components.ND) score -= (parseFloat(correctionND) || 0);
    return score;
  };

  const handleCorrectionSubmit = async () => {
    const components = competition?.scoreComponents || { D: true, E: true, B: false, ND: true };
    if (!correctionNumber) {
      toast.error('Please fill in the gymnast number');
      return;
    }
    if (components.D && !correctionD) {
      toast.error('Please fill in the D score');
      return;
    }
    if (components.E && !correctionE) {
      toast.error('Please fill in the E score');
      return;
    }
    
    setCorrectionSubmitting(true);
    try {
      const response = await axios.post(`${API}/scoring/correction`, {
        competitionId,
        apparatus: effectiveApparatus,
        panel,
        gymnastNumber: correctionNumber,
        gymnastLetter: (panel && panel !== 'single' && panel !== 'master') ? panel.toUpperCase() : '',
        difficulty: parseFloat(correctionD) || 0,
        execution: parseFloat(correctionE) || 0,
        bonus: parseFloat(correctionB) || 0,
        neutralDeductions: parseFloat(correctionND) || 0
      });
      
      const newScore = response.data?.newScore;
      const gymnastName = response.data?.gymnastName || `#${correctionNumber}`;
      
      toast.success(`Score corrected for ${gymnastName}: ${newScore ? newScore.toFixed(3) : calculateCorrectionFinal().toFixed(3)}`);
      
      setShowCorrectionDialog(false);
      setShowCorrectionConfirm(false);
      setCorrectionNumber('');
      setCorrectionD('');
      setCorrectionE('');
      setCorrectionB('0');
      setCorrectionND('0');
      
      // Refresh the scored gymnasts list
      fetchRotationGymnasts();
    } catch (error) {
      console.error('Correction error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit correction');
    } finally {
      setCorrectionSubmitting(false);
    }
  };

  // Get unscored gymnasts
  const unscoredGymnasts = gymnasts.filter(g => !scoredGymnastIds.includes(g.gymnastId));

  // Fullscreen restore — MUST be before all early returns (React hooks rules)
  useEffect(() => {
    if (!pendingFullscreen) return;
    const handler = () => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch(() => {});
      }
      setPendingFullscreen(false);
    };
    document.addEventListener('click', handler, { once: true });
    document.addEventListener('touchstart', handler, { once: true });
    document.addEventListener('keydown', handler, { once: true });
    return () => {
      document.removeEventListener('click', handler);
      document.removeEventListener('touchstart', handler);
      document.removeEventListener('keydown', handler);
    };
  }, [pendingFullscreen]);

  // Scoring lock overlay — shown when operator locks scoring from /open
  if (scoringLocked && pinEntered) {
    return (
      <div className="fixed inset-0 z-50 bg-white flex flex-col items-center justify-center select-none">
        <img src="/scorz-logo.png" alt="Scorz" className="h-16 mb-8" draggable={false} />
        <div className="flex items-center gap-3 mb-3">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
          <p className="text-gray-900 text-3xl font-black">Scoring Locked</p>
        </div>
        <p className="text-gray-400 text-lg">Scoring has been paused by the control desk.</p>
        <p className="text-gray-400 text-base mt-1">Please wait for scoring to be unlocked.</p>
      </div>
    );
  }

  // PIN Entry Screen - Full screen, centered
  if (!pinEntered) {
    return (
      <div className="h-screen w-screen bg-white flex items-center justify-center overflow-hidden">
        <div className="text-center">
          <img src="/scorz-logo.png" alt="Scorz" className="h-16 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-black mb-6">{headerApparatusLabel}</h1>
          
          <form onSubmit={handlePinSubmit} className="space-y-4 w-72">
            <div>
              <Label htmlFor="pin" className="text-sm block mb-2">Enter Judge Code</Label>
              <Input
                id="pin"
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                className="text-center text-2xl tracking-widest h-12"
                autoFocus
                data-testid="judge-pin-input"
              />
            </div>
            <Button type="submit" className="w-full bg-black hover:bg-gray-800 h-10" data-testid="judge-pin-submit">
              Enter
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // Splash Screen - Full screen, 2.5 seconds
  if (showSplash && selectedGymnast) {
    return (
      <div className="h-screen w-screen bg-white flex flex-col items-center justify-center overflow-hidden">
        <div className="text-center">
          {/* Large Number */}
          <div className="text-[140px] font-bold text-black leading-none">
            {selectedGymnast.number}{selectedGymnast.letter || ''}
          </div>
          
          {/* Name */}
          <h1 className="text-4xl font-bold text-black mt-2">
            {selectedGymnast.name}
          </h1>
          
          {/* Club */}
          <p className="text-2xl text-gray-600 mt-1">
            {selectedGymnast.club}
          </p>
          
          {/* Category & Round */}
          <div className="text-base text-gray-400 mt-4">
            {selectedGymnast.category || 'Open'} • Round {roundNumber}
          </div>
        </div>
      </div>
    );
  }

  // Scoring Screen - Full screen, landscape optimized with on-screen keypad
  if (selectedGymnast) {
    const finalScore = calculateFinalScore();
    
    return (
      <div className="h-screen w-screen bg-gray-50 flex flex-col overflow-hidden">
      {/* Full-screen tap-to-continue overlay — replaces thin banner */}
        {pendingFullscreen && (
          <div
            className="fixed inset-0 z-[100] bg-black flex flex-col items-center justify-center cursor-pointer select-none"
            onClick={() => { document.documentElement.requestFullscreen().catch(() => {}); setPendingFullscreen(false); }}
            onTouchStart={() => { document.documentElement.requestFullscreen().catch(() => {}); setPendingFullscreen(false); }}
            data-testid="fullscreen-banner"
          >
            <img src="/scorz-logo.png" alt="Scorz" className="h-20 mb-8 invert" draggable={false} />
            <p className="text-white text-2xl font-bold tracking-wide">Press anywhere to continue</p>
          </div>
        )}
        {/* Compact Header */}
        <div className="bg-white border-b px-4 py-2 flex items-center gap-3 shrink-0">
          <Button variant="ghost" size="sm" onClick={handleBack} className="p-1.5">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          
          <div className="flex-1 flex items-center gap-3">
            <div className="bg-black text-white font-bold text-lg px-3 py-1.5 rounded">
              {selectedGymnast.number}{selectedGymnast.letter || ''}
            </div>
            <div>
              <span className="font-bold">{selectedGymnast.name}</span>
              <span className="text-gray-500 ml-2 text-sm">• {selectedGymnast.club}</span>
              {selectedGymnast.category && (
                <span className="text-gray-400 ml-2 text-sm">• {selectedGymnast.category}</span>
              )}
            </div>
          </div>
          
          <button
            onClick={markRotationFinished}
            className={`h-8 w-8 flex items-center justify-center rounded text-white shrink-0 transition-colors ${
              rotationMarkedFinished ? 'bg-green-800 ring-2 ring-green-400 ring-offset-1' : 'bg-green-600 hover:bg-green-700'
            }`}
            title={rotationMarkedFinished ? 'Rotation Finished ✓' : 'Finished Rotation — mark this rotation as complete'}
            data-testid="judge-tick-btn"
          >
            <Check className="w-4 h-4 stroke-[3]" />
          </button>

          <Button
            variant="outline"
            size="sm"
            onClick={toggleFullscreen}
            className="h-8 w-8 p-0"
          >
            {isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
          </Button>
          
          <Button
            variant="outline"
            onClick={openCorrectionDialog}
            className="text-blue-600 border-blue-600 hover:bg-blue-50 h-8 px-3 text-sm"
          >
            <Edit3 className="w-4 h-4 mr-1" />
            Correction
          </Button>
          
          <Button
            variant="outline"
            onClick={handleHelpRequest}
            className="text-orange-600 border-orange-600 hover:bg-orange-50 h-8 px-3 text-sm"
          >
            <HelpCircle className="w-4 h-4 mr-1" />
            Help
          </Button>
        </div>

        {/* Vault 1 / Vault 2 tabs — visible when this gymnast's category has vaultCount=2 */}
        {(/vault/i.test(effectiveApparatus || '') && Number(selectedGymnast?.vaultCount) === 2) && (
          <div className="bg-white border-b px-4 py-2 flex items-center gap-2 shrink-0" data-testid="vault-attempt-tabs">
            <span className="text-xs uppercase tracking-wide text-gray-500 mr-2">Attempt</span>
            {[1, 2].map(n => {
              const submitted = scoredVaultAttempts.has(`${selectedGymnast.gymnastId}|${n}`);
              const isActive = vaultAttempt === n;
              return (
                <button
                  key={n}
                  type="button"
                  data-testid={`vault-attempt-tab-${n}`}
                  onClick={() => switchVaultAttempt(n)}
                  className={`px-4 py-1.5 text-sm rounded-md border transition ${isActive ? 'bg-black text-white border-black' : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                >
                  Vault {n}
                  {submitted && (
                    <Check className={`w-3.5 h-3.5 ml-1 inline ${isActive ? 'text-white' : 'text-green-600'}`} />
                  )}
                </button>
              );
            })}
            <span className="ml-auto text-xs text-gray-500">
              Ranking rule: <b>{selectedGymnast?.vaultRanking || 'first'}</b>
              {selectedGymnast?.vaultForAATeam && selectedGymnast.vaultForAATeam !== selectedGymnast.vaultRanking && (
                <> · AA/team uses <b>{selectedGymnast.vaultForAATeam}</b></>
              )}
            </span>
          </div>
        )}

        {/* Scoring Content - Main area */}
        <div className="flex-1 flex overflow-hidden">
          {currentScore.didNotCompete ? (
            // DNC State - Centered
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Ban className="w-20 h-20 mx-auto mb-4 text-gray-400" />
                <p className="text-2xl font-semibold text-gray-600 mb-6">Did Not Compete</p>
                <div className="flex gap-4 justify-center">
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => setCurrentScore(prev => ({ ...prev, didNotCompete: false, difficulty: '', execution: '', execution1: '', execution2: '', execution3: '', execution4: '', execution5: '' }))}
                    className="h-12 px-6"
                  >
                    Clear DNC
                  </Button>
                  <Button
                    onClick={handleSubmitScore}
                    disabled={submitting}
                    className="bg-green-600 hover:bg-green-700 h-12 px-8 text-lg"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    Submit DNC
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            // Score Entry - Same layout as correction dialog
            <>
              {/* Left Side - Score Fields */}
              <div className="flex-1 p-4 flex flex-col">
                {/* Score Fields */}
                <div className="flex-1 flex flex-col justify-center max-w-xl mx-auto w-full">
                  {/* Score Input Fields - Dynamic based on competition config */}
                  {(() => {
                    const components = competition?.scoreComponents || { D: true, E: true, B: false, ND: true };
                    const eCount = judgeConfig.eJudges || 1;
                    const multiE = eCount > 1 && components.E !== false;

                    const fieldBox = (label, value, fieldKey, locked = false) => (
                      <div key={fieldKey}>
                        <Label className="text-xs font-medium text-gray-600">{label}</Label>
                        <div
                          onClick={locked ? undefined : () => setActiveScoringField(fieldKey)}
                          className={`mt-1 h-14 px-2 flex items-center justify-center text-2xl font-mono font-bold rounded-lg border-2 transition-all ${
                            locked
                              ? 'border-gray-100 bg-gray-50 text-gray-500 cursor-default'
                              : activeScoringField === fieldKey
                              ? 'border-blue-500 bg-blue-50 cursor-pointer'
                              : 'border-gray-200 bg-white cursor-pointer'
                          }`}
                          data-testid={`score-${fieldKey}`}
                        >
                          {value || <span className="text-gray-300">–</span>}
                        </div>
                      </div>
                    );

                    if (!multiE) {
                      // Single E — original 2×2 or row layout
                      return (
                        <div className={`grid gap-3 mb-4 ${
                          [components.D !== false, true, components.B, components.ND !== false].filter(Boolean).length <= 2
                            ? 'grid-cols-2'
                            : [components.D !== false, true, components.B, components.ND !== false].filter(Boolean).length === 3
                            ? 'grid-cols-3' : 'grid-cols-2 sm:grid-cols-4'
                        }`}>
                          {components.D !== false && fieldBox('D (Difficulty)', currentScore.difficulty || '', 'd')}
                          {components.E !== false && fieldBox('E (Execution)', currentScore.execution || '', 'e')}
                          {components.B && fieldBox('B (Bonus)', currentScore.bonus || '', 'b')}
                          {components.ND !== false && fieldBox('ND (Neutral)', currentScore.neutralDeductions || '', 'nd')}
                        </div>
                      );
                    }

                    // Multi-E layout:
                    // Row 1: D + E1…En  (all editable)
                    // Row 2: E AVG (locked) + [B] + ND
                    const row1Items = [
                      components.D !== false ? fieldBox('D', currentScore.difficulty || '', 'd') : null,
                      ...Array.from({ length: eCount }, (_, i) => {
                        const fi = `e${i + 1}`;
                        return fieldBox(`E${i + 1}`, currentScore[`execution${i + 1}`] || '', fi);
                      }),
                    ].filter(Boolean);

                    const row2Items = [
                      fieldBox(`E AVG ÷${eCount}`, currentScore.execution || '', 'eavg', true),
                      components.B ? fieldBox('B (Bonus)', currentScore.bonus || '', 'b') : null,
                      components.ND !== false ? fieldBox('ND', currentScore.neutralDeductions || '', 'nd') : null,
                    ].filter(Boolean);

                    return (
                      <div className="mb-4 space-y-2">
                        <div className={`grid gap-2`} style={{ gridTemplateColumns: `repeat(${row1Items.length}, 1fr)` }}>
                          {row1Items}
                        </div>
                        <div className={`grid gap-2`} style={{ gridTemplateColumns: `repeat(${row2Items.length}, 1fr)` }}>
                          {row2Items}
                        </div>
                      </div>
                    );
                  })()}
                  
                  {/* Formula & Final Score - Dynamic */}
                  <div className="bg-gray-100 rounded-lg p-4 flex items-center justify-between mb-4">
                    <div className="text-sm text-gray-500">
                      {parseFloat(competition?.scoreBaseValue ?? 10).toFixed(3)}
                      {(competition?.scoreComponents?.D !== false) && <> + <span className="font-semibold text-black">{currentScore.difficulty || '0'}</span></>}
                      {(competition?.scoreComponents?.E !== false) && (
                        judgeConfig.eJudges > 1
                          ? <> − <span className="font-semibold text-black">E AVG({currentScore.execution || '?'})</span></>
                          : <> − <span className="font-semibold text-black">{currentScore.execution || '0'}</span></>
                      )}
                      {competition?.scoreComponents?.B && <> + <span className="font-semibold text-black">{currentScore.bonus || '0'}</span></>}
                      {(competition?.scoreComponents?.ND !== false) && <> − <span className="font-semibold text-black">{currentScore.neutralDeductions || '0'}</span></>}
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Final Score</p>
                      {canSubmit() ? (
                        <p className="text-4xl font-bold font-mono">{finalScore}</p>
                      ) : (
                        <p className="text-4xl font-bold font-mono text-gray-300">—</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      className="flex-1 h-12 text-gray-500 border-gray-300"
                      onClick={markDidNotCompete}
                    >
                      <Ban className="w-4 h-4 mr-2" />
                      DNC
                    </Button>
                    <Button
                      onClick={handleSubmitScore}
                      disabled={submitting || !canSubmit()}
                      className="flex-[2] h-12 text-lg bg-green-600 hover:bg-green-700 disabled:bg-gray-300"
                      data-testid="submit-score-btn"
                    >
                      {submitting ? 'Submitting...' : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Submit Score
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Right Side - Numeric Keypad */}
              <div className="w-64 bg-white border-l p-4 flex flex-col">
                <p className="text-xs text-gray-500 text-center mb-3 font-medium">
                  {activeScoringField === 'd' ? 'Difficulty (D)'
                    : /^e[1-5]$/.test(activeScoringField) ? `Execution ${activeScoringField.toUpperCase()}`
                    : activeScoringField === 'e' ? 'Execution (E)'
                    : activeScoringField === 'b' ? 'Bonus (B)'
                    : 'Neutral Deduction (ND)'}
                </p>
                <div className="grid grid-cols-3 gap-2 flex-1">
                  {['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', '⌫'].map((key) => (
                    <button
                      key={key}
                      onClick={() => handleScoringKeypadPress(key)}
                      className={`rounded-xl text-2xl font-bold transition-all active:scale-95 flex items-center justify-center ${
                        key === '⌫' 
                          ? 'bg-red-100 text-red-600 hover:bg-red-200' 
                          : key === '.'
                          ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          : 'bg-gray-100 text-black hover:bg-gray-200'
                      }`}
                      style={{ aspectRatio: '1' }}
                    >
                      {key}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => handleScoringKeypadPress('C')}
                  className="mt-2 h-12 bg-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-400 transition-all"
                >
                  Clear
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // Main Tiles View - Full screen, no scroll, landscape optimized
  return (
    <div className="h-screen w-screen bg-gray-100 flex flex-col overflow-hidden">
      {/* Full-screen tap-to-continue overlay — replaces thin banner */}
      {pendingFullscreen && (
        <div
          className="fixed inset-0 z-[100] bg-black flex flex-col items-center justify-center cursor-pointer select-none"
          onClick={() => { document.documentElement.requestFullscreen().catch(() => {}); setPendingFullscreen(false); }}
          onTouchStart={() => { document.documentElement.requestFullscreen().catch(() => {}); setPendingFullscreen(false); }}
          data-testid="fullscreen-banner"
        >
          <img src="/scorz-logo.png" alt="Scorz" className="h-20 mb-8 invert" draggable={false} />
          <p className="text-white text-2xl font-bold tracking-wide">Press anywhere to continue</p>
        </div>
      )}
      {/* Compact Header */}
      <div className="bg-white border-b shrink-0">
        <div className="flex items-center justify-between px-4 py-2">
          <div className="flex items-center gap-3">
            <img src="/scorz-logo.png" alt="Scorz" className="h-7" />
            <h1 className="text-lg font-bold text-black">{headerApparatusLabel}</h1>
            {panel && panel !== 'single' && panel !== 'master' && (
              <Badge className="bg-blue-600 text-white text-xs px-2 py-0.5">Panel {panel.toUpperCase()}</Badge>
            )}
            {panel === 'master' && (
              <Badge className="bg-purple-600 text-white text-xs px-2 py-0.5">Master</Badge>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {/* Round/Rotation Selectors - compact */}
            <div className="flex items-center gap-1 bg-gray-100 rounded px-2 py-1">
              <span className="text-xs text-gray-500">Round:</span>
              <select 
                value={roundNumber}
                onChange={(e) => { setRoundNumber(parseInt(e.target.value)); setScoredGymnastIds([]); }}
                className="bg-white border rounded px-1.5 py-0.5 text-sm h-7 font-medium"
                data-testid="round-select"
              >
                {(availableRounds.length > 0 ? availableRounds : [1, 2, 3, 4, 5]).map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-1 bg-gray-100 rounded px-2 py-1">
              <span className="text-xs text-gray-500">Rotation:</span>
              <select
                value={rotationNumber}
                onChange={(e) => { setRotationNumber(parseInt(e.target.value)); setScoredGymnastIds([]); }}
                className="bg-white border rounded px-1.5 py-0.5 text-sm h-7 font-medium"
                data-testid="rotation-select"
              >
                {(() => {
                  const maxRot = rotationsByRound[roundNumber] || 0;
                  const opts = maxRot > 0
                    ? Array.from({ length: maxRot }, (_, i) => i + 1)
                    : [1, 2, 3, 4, 5];
                  return opts.map(r => <option key={r} value={r}>{r}</option>);
                })()}
              </select>
            </div>

            {/* MAG: tiny apparatus picker — only when multiple MAG apparatuses share the rotation */}
            {isMag && magActiveApparatuses.length > 1 && (
              <div className="flex items-center gap-1 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="mag-apparatus-picker">
                <span className="text-xs text-amber-700">Apparatus:</span>
                <select
                  value={magResolvedApparatus}
                  onChange={(e) => { setMagResolvedApparatus(e.target.value); setScoredGymnastIds([]); }}
                  className="bg-white border rounded px-1.5 py-0.5 text-sm h-7 font-medium"
                  data-testid="mag-apparatus-select"
                >
                  <option value="">Pick…</option>
                  {magActiveApparatuses.map(a => (
                    <option key={a} value={a}>{apparatusNames[a]}</option>
                  ))}
                </select>
              </div>
            )}
            {isMag && magActiveApparatuses.length === 1 && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="mag-apparatus-auto">
                {apparatusNames[magResolvedApparatus]}
              </div>
            )}
            
            <button
              onClick={markRotationFinished}
              className={`h-8 w-8 flex items-center justify-center rounded text-white shrink-0 transition-colors ${
                rotationMarkedFinished ? 'bg-green-800 ring-2 ring-green-400 ring-offset-1' : 'bg-green-600 hover:bg-green-700'
              }`}
              title={rotationMarkedFinished ? 'Rotation Finished ✓' : 'Finished Rotation — mark this rotation as complete'}
              data-testid="judge-tick-btn"
            >
              <Check className="w-4 h-4 stroke-[3]" />
            </button>

            <Button
              variant="outline"
              size="sm"
              onClick={toggleFullscreen}
              className="h-8 w-8 p-0"
            >
              {isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
            </Button>
            
            <Button
              variant="outline"
              onClick={openCorrectionDialog}
              className="text-blue-600 border-blue-600 hover:bg-blue-50 h-8 px-3 text-sm"
            >
              <Edit3 className="w-4 h-4 mr-1" />
              Correction
            </Button>

            <Button
              variant="outline"
              onClick={handleHelpRequest}
              className="text-orange-600 border-orange-600 hover:bg-orange-50 h-8 px-3 text-sm"
            >
              <HelpCircle className="w-4 h-4 mr-1" />
              Help
            </Button>
          </div>
        </div>
      </div>

      {/* Content - Fills remaining space */}
      <div className="flex-1 p-4 flex flex-col min-h-0">
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-gray-500">Loading gymnasts...</div>
          </div>
        ) : gymnasts.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <AlertCircle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-600 text-lg">No gymnasts found for this rotation</p>
              <p className="text-sm text-gray-400 mt-1">Check rotation schedule setup</p>
            </div>
          </div>
        ) : unscoredGymnasts.length === 0 ? (
          // All gymnasts scored
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Check className="w-20 h-20 mx-auto mb-4 text-green-500" />
              <p className="text-2xl font-semibold text-gray-800 mb-2">Rotation Complete!</p>
              <p className="text-gray-600 mb-4">
                All {gymnasts.length} gymnasts have been scored.
              </p>
              {(() => {
                const maxRot = rotationsByRound[roundNumber] || 5;
                return rotationNumber < maxRot ? (
                  <Button
                    onClick={() => { setRotationNumber(prev => prev + 1); setScoredGymnastIds([]); }}
                    className="bg-black hover:bg-gray-800 h-12 px-8 text-lg"
                  >
                    <ChevronRight className="w-5 h-5 mr-1" />
                    Go to Rotation {rotationNumber + 1}
                  </Button>
                ) : null;
              })()}
            </div>
          </div>
        ) : (
          <>
            {/* Info Bar */}
            <div className="flex items-center justify-between mb-3 shrink-0">
              <div className="flex items-center gap-2">
                {groups.map(g => (
                  <Badge key={g} variant="secondary" className="text-xs">{g}</Badge>
                ))}
              </div>
              <span className="text-sm text-gray-500">
                {unscoredGymnasts.length} remaining • {scoredGymnastIds.length} scored
              </span>
            </div>

            {/* Tiles Grid - Fills available space */}
            <div className="flex-1 min-h-0">
              <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2 h-full content-start auto-rows-min">
                {unscoredGymnasts.map((gymnast) => (
                  <button
                    key={gymnast.gymnastId}
                    onClick={() => handleSelectGymnast(gymnast)}
                    className="bg-black text-white rounded-lg flex flex-col items-center justify-center py-2 px-2 hover:bg-gray-800 transition-all hover:scale-105 active:scale-95 shadow-md"
                    data-testid={`gymnast-tile-${gymnast.number}${gymnast.letter || ''}`}
                  >
                    <span className="text-xl font-bold leading-none">{gymnast.number}{gymnast.letter || ''}</span>
                    <span className="text-[10px] text-gray-200 truncate w-full text-center mt-0.5 leading-tight">{gymnast.name}</span>
                    <span className="text-[9px] text-gray-400 truncate w-full text-center leading-tight">{gymnast.club}</span>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Scored Gymnasts - Fixed at bottom */}
            <div className="pt-3 border-t mt-3 shrink-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-gray-400">Scored:</span>
                {gymnasts
                  .filter(g => scoredGymnastIds.includes(g.gymnastId))
                  .map(g => (
                    <span
                      key={g.gymnastId}
                      className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs font-medium inline-flex items-center gap-1"
                    >
                      <Check className="w-3 h-3" />
                      {g.number}{g.letter || ''}
                    </span>
                  ))
                }
                {scoredGymnastIds.length === 0 && (
                  <span className="text-xs text-gray-300 italic">none yet</span>
                )}
                <button
                  type="button"
                  onClick={openSubmittedDrawer}
                  className="ml-auto text-xs text-gray-400 hover:text-gray-700 underline-offset-2 hover:underline inline-flex items-center gap-1"
                  data-testid="view-submitted-scores-btn"
                  title="View scores submitted on this panel"
                >
                  <ListChecks className="w-3 h-3" />
                  View submitted
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Correction Dialog - Landscape Optimized */}
      <Dialog open={showCorrectionDialog} onOpenChange={setShowCorrectionDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0 overflow-hidden">
          <div className="flex h-full">
            {/* Left Side - Form Fields */}
            <div className="flex-1 p-4 flex flex-col">
              <DialogHeader className="pb-2">
                <DialogTitle className="flex items-center gap-2 text-lg">
                  <Edit3 className="w-5 h-5 text-blue-600" />
                  Score Correction
                </DialogTitle>
              </DialogHeader>
              
              {!showCorrectionConfirm ? (
                <div className="flex-1 flex flex-col gap-3">
                  {/* Gymnast Number */}
                  <div>
                    <Label className="text-xs font-medium text-gray-600">Gymnast #</Label>
                    <div 
                      onClick={() => setActiveField('number')}
                      className={`mt-1 h-12 px-3 flex items-center justify-center text-xl font-bold rounded-lg border-2 cursor-pointer transition-all ${
                        activeField === 'number' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'
                      }`}
                    >
                      {correctionNumber ? (
                        <span>{correctionNumber}{panel && panel !== 'single' && panel !== 'master' ? ` ${panel.toUpperCase()}` : ''}</span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </div>
                    {panel && panel !== 'single' && panel !== 'master' && (
                      <p className="text-xs text-gray-500 mt-1">Panel {panel.toUpperCase()} (auto-assigned)</p>
                    )}
                  </div>
                  
                  {/* Score Fields Row - Dynamic */}
                  <div className={`grid gap-2 ${
                    [competition?.scoreComponents?.D !== false, competition?.scoreComponents?.E !== false, competition?.scoreComponents?.B, competition?.scoreComponents?.ND !== false].filter(Boolean).length <= 2 ? 'grid-cols-2' :
                    [competition?.scoreComponents?.D !== false, competition?.scoreComponents?.E !== false, competition?.scoreComponents?.B, competition?.scoreComponents?.ND !== false].filter(Boolean).length === 3 ? 'grid-cols-3' : 'grid-cols-2 sm:grid-cols-4'
                  }`}>
                    {(competition?.scoreComponents?.D !== false) && (
                    <div>
                      <Label className="text-xs font-medium text-gray-600">D (Difficulty)</Label>
                      <div 
                        onClick={() => setActiveField('d')}
                        className={`mt-1 h-14 px-3 flex items-center justify-center text-2xl font-mono font-bold rounded-lg border-2 cursor-pointer transition-all ${
                          activeField === 'd' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'
                        }`}
                      >
                        {correctionD || <span className="text-gray-300">0.000</span>}
                      </div>
                    </div>
                    )}
                    {(competition?.scoreComponents?.E !== false) && (
                    <div>
                      <Label className="text-xs font-medium text-gray-600">E (Execution)</Label>
                      <div 
                        onClick={() => setActiveField('e')}
                        className={`mt-1 h-14 px-3 flex items-center justify-center text-2xl font-mono font-bold rounded-lg border-2 cursor-pointer transition-all ${
                          activeField === 'e' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'
                        }`}
                      >
                        {correctionE || <span className="text-gray-300">0.000</span>}
                      </div>
                    </div>
                    )}
                    {competition?.scoreComponents?.B && (
                    <div>
                      <Label className="text-xs font-medium text-gray-600">B (Bonus)</Label>
                      <div 
                        onClick={() => setActiveField('b')}
                        className={`mt-1 h-14 px-3 flex items-center justify-center text-2xl font-mono font-bold rounded-lg border-2 cursor-pointer transition-all ${
                          activeField === 'b' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'
                        }`}
                      >
                        {correctionB || <span className="text-gray-300">0.000</span>}
                      </div>
                    </div>
                    )}
                    {(competition?.scoreComponents?.ND !== false) && (
                    <div>
                      <Label className="text-xs font-medium text-gray-600">ND (Neutral)</Label>
                      <div 
                        onClick={() => setActiveField('nd')}
                        className={`mt-1 h-14 px-3 flex items-center justify-center text-2xl font-mono font-bold rounded-lg border-2 cursor-pointer transition-all ${
                          activeField === 'nd' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'
                        }`}
                      >
                        {correctionND || <span className="text-gray-300">0.0</span>}
                      </div>
                    </div>
                    )}
                  </div>
                  
                  {/* Formula & Final Score - Dynamic */}
                  <div className="bg-gray-100 rounded-lg p-3 flex items-center justify-between">
                    <div className="text-sm text-gray-500">
                      {parseFloat(competition?.scoreBaseValue ?? 10).toFixed(3)}
                      {(competition?.scoreComponents?.D !== false) && <> + <span className="font-semibold text-black">{correctionD || '0'}</span></>}
                      {(competition?.scoreComponents?.E !== false) && <> − <span className="font-semibold text-black">{correctionE || '0'}</span></>}
                      {competition?.scoreComponents?.B && <> + <span className="font-semibold text-black">{correctionB || '0'}</span></>}
                      {(competition?.scoreComponents?.ND !== false) && <> − <span className="font-semibold text-black">{correctionND || '0'}</span></>}
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Final Score</p>
                      <p className="text-2xl font-bold font-mono">{calculateCorrectionFinal().toFixed(3)}</p>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex gap-2 mt-auto">
                    <Button variant="outline" onClick={() => setShowCorrectionDialog(false)} className="flex-1 h-10">
                      Cancel
                    </Button>
                    <Button
                      className="flex-1 h-10 bg-blue-600 hover:bg-blue-700"
                      onClick={() => setShowCorrectionConfirm(true)}
                      disabled={!correctionNumber || ((competition?.scoreComponents?.D !== false) && !correctionD) || ((competition?.scoreComponents?.E !== false) && !correctionE)}
                    >
                      Review
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center">
                  <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg text-center w-full">
                    <p className="text-lg font-semibold text-yellow-800">Confirm Correction</p>
                    <p className="text-sm text-yellow-700 mt-2">
                      Gymnast <strong>#{correctionNumber}{(panel && panel !== 'single' && panel !== 'master') ? ` ${panel.toUpperCase()}` : ''}</strong> on <strong>{headerApparatusLabel}</strong>
                    </p>
                    <div className="mt-3 text-3xl font-bold text-black font-mono">
                      {calculateCorrectionFinal().toFixed(3)}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {parseFloat(competition?.scoreBaseValue ?? 10).toFixed(3)}
                      {(competition?.scoreComponents?.D !== false) ? ` + ${correctionD || '0'}` : ''}
                      {(competition?.scoreComponents?.E !== false) ? ` − ${correctionE || '0'}` : ''}
                      {competition?.scoreComponents?.B ? ` + ${correctionB || '0'}` : ''}
                      {(competition?.scoreComponents?.ND !== false) ? ` − ${correctionND || '0'}` : ''}
                    </p>
                  </div>
                  <div className="flex gap-2 mt-4 w-full">
                    <Button variant="outline" onClick={() => setShowCorrectionConfirm(false)} className="flex-1 h-10">
                      Back
                    </Button>
                    <Button
                      className="flex-1 h-10 bg-green-600 hover:bg-green-700"
                      onClick={handleCorrectionSubmit}
                      disabled={correctionSubmitting}
                    >
                      {correctionSubmitting ? 'Submitting...' : 'Confirm'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
            
            {/* Right Side - Numeric Keypad */}
            {!showCorrectionConfirm && (
              <div className="w-56 bg-gray-50 border-l p-3 flex flex-col">
                <p className="text-xs text-gray-500 text-center mb-2 font-medium">
                  {activeField === 'number' ? 'Gymnast #' : activeField === 'd' ? 'D Score' : activeField === 'e' ? 'E Score' : activeField === 'nd' ? 'ND Score' : 'Select Field'}
                </p>
                <div className="grid grid-cols-3 gap-1.5 flex-1">
                  {['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', '⌫'].map((key) => (
                    <button
                      key={key}
                      onClick={() => handleKeypadPress(key)}
                      className={`rounded-lg text-xl font-bold transition-all active:scale-95 ${
                        key === '⌫' 
                          ? 'bg-red-100 text-red-600 hover:bg-red-200' 
                          : key === '.'
                          ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          : 'bg-white text-black hover:bg-gray-100 border border-gray-200'
                      }`}
                      style={{ aspectRatio: '1' }}
                    >
                      {key}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => handleKeypadPress('C')}
                  className="mt-1.5 h-10 bg-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-400 transition-all"
                >
                  Clear
                </button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Submitted Scores Drawer — right-side slide-in, fullscreen friendly */}
      {showSubmitted && (
        <div
          className="fixed inset-0 z-[9998] flex"
          data-testid="submitted-scores-overlay"
        >
          {/* Dim click-catcher */}
          <div
            className="flex-1 bg-black/40"
            onClick={() => setShowSubmitted(false)}
          />
          {/* Panel */}
          <div className="w-full max-w-md h-full bg-white shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
              <div>
                <h2 className="text-base font-bold">Submitted Scores</h2>
                <p className="text-[11px] text-gray-500">
                  {headerApparatusLabel}
                  {panel && panel !== 'single' && panel !== 'master' && ` • Panel ${panel.toUpperCase()}`}
                  {panel === 'master' && ' • Master'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchSubmittedScores}
                  disabled={submittedLoading}
                  className="h-8 w-8 p-0"
                  title="Refresh"
                  data-testid="refresh-submitted-btn"
                >
                  <RefreshCw className={`w-4 h-4 ${submittedLoading ? 'animate-spin' : ''}`} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSubmitted(false)}
                  className="h-8 w-8 p-0"
                  data-testid="close-submitted-btn"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {submittedLoading && submittedScores.length === 0 ? (
                <p className="text-center text-gray-500 py-10">Loading…</p>
              ) : submittedScores.length === 0 ? (
                <p className="text-center text-gray-500 py-10">No scores submitted yet on this panel.</p>
              ) : (
                <ul className="divide-y">
                  {submittedScores.map((s) => (
                    <li key={s.id} className="px-4 py-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono bg-gray-100 rounded px-1.5 py-0.5">
                              #{s.gymnastNumber || '—'}
                            </span>
                            <span className="font-semibold truncate">{s.gymnastName}</span>
                          </div>
                          <div className="text-[11px] text-gray-500 mt-0.5">
                            R{s.roundNumber ?? '—'} · Rotation {s.rotationNumber ?? '—'}
                            {s.submittedAt && ` · ${new Date(s.submittedAt).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit', second: '2-digit'})}`}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-lg font-bold ${s.didNotCompete ? 'text-gray-400' : 'text-black'}`}>
                            {s.didNotCompete ? 'DNC' : (s.finalScore != null ? parseFloat(s.finalScore).toFixed(3) : '—')}
                          </div>
                          {!s.didNotCompete && (
                            <div className="text-[10px] text-gray-500 font-mono">
                              D {parseFloat(s.difficulty || 0).toFixed(1)} · E {parseFloat(s.execution || 0).toFixed(1)}
                              {parseFloat(s.neutralDeductions || 0) > 0 && ` · ND ${parseFloat(s.neutralDeductions).toFixed(1)}`}
                            </div>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="px-4 py-2 border-t bg-gray-50 text-[11px] text-gray-500">
              Showing the most recent {submittedScores.length} score{submittedScores.length === 1 ? '' : 's'} for this apparatus and panel.
            </div>
          </div>
        </div>
      )}

      {/* Training Video Overlay */}
      {showVideo && videoUrl && (
        <div className="fixed inset-0 bg-black z-[9999] flex items-center justify-center">
          <div className="w-full h-full flex flex-col">
            {/* Header */}
            <div className="bg-black text-white py-4 px-6 flex items-center justify-between">
              <div className="flex items-center">
                <img src="/scorz-logo.png" alt="Scorz" className="h-8 mr-4 invert" />
                <h2 className="text-xl font-semibold">Judge Training Video</h2>
              </div>
              {/* No close button - judges must watch */}
            </div>
            
            {/* Video Container */}
            <div className="flex-1 flex items-center justify-center bg-black relative">
              {/* Countdown Overlay - shown before video plays, hidden when video is playing */}
              {!videoIsPlaying && videoCountdown !== null && videoCountdown > 0 && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-black z-10">
                  <div className="text-white text-center">
                    <img src="/scorz-logo.png" alt="Scorz" className="h-24 mx-auto mb-8 invert" />
                    <p className="text-2xl text-gray-300">
                      {videoReady ? 'The video is starting soon' : 'Loading video...'}
                    </p>
                  </div>
                </div>
              )}
              
              {/* Video Element */}
              <video
                ref={videoRef}
                src={videoUrl}
                playsInline
                controls={false}
                preload="auto"
                onEnded={handleTrainingVideoEnd}
                onError={(e) => {
                  const error = e.target.error;
                  let errorMessage = 'Unknown error';
                  if (error) {
                    switch (error.code) {
                      case 1: errorMessage = 'Video loading aborted'; break;
                      case 2: errorMessage = 'Network error while loading video'; break;
                      case 3: errorMessage = 'Video decoding failed - format may not be supported'; break;
                      case 4: errorMessage = 'Video format not supported by this browser'; break;
                      default: errorMessage = error.message || 'Unknown error';
                    }
                  }
                  console.error('[Video Sync] Video error:', error?.code, errorMessage);
                  console.error('[Video Sync] Video src:', videoUrl);
                  setVideoError(errorMessage);
                }}
                onLoadStart={() => {
                  console.log('[Video Sync] Video loading started:', videoUrl);
                  setVideoError(null);
                }}
                onLoadedData={() => console.log('[Video Sync] Video data loaded')}
                onLoadedMetadata={() => console.log('[Video Sync] Video metadata loaded')}
                onCanPlay={() => {
                  console.log('[Video Sync] Video can play - ready for synchronized start');
                  setVideoReady(true);
                  setVideoError(null);
                }}
                onCanPlayThrough={() => console.log('[Video Sync] Video can play through')}
                onWaiting={() => console.log('[Video Sync] Video waiting/buffering')}
                onPlaying={() => {
                  console.log('[Video Sync] Video is playing');
                  setVideoIsPlaying(true);
                  setVideoCountdown(null); // Ensure countdown is cleared
                  setVideoError(null);
                }}
                className="max-w-full max-h-full"
                style={{ maxHeight: 'calc(100vh - 100px)' }}
              />
            </div>
            
            {/* Footer */}
            <div className="bg-black text-white py-3 px-6 text-center text-sm text-gray-400">
              {videoError 
                ? <span className="text-red-400">Error: {videoError}</span>
                : videoCountdown !== null && videoCountdown > 0 
                  ? `Starting in ${videoCountdown} seconds...` 
                  : !videoReady 
                    ? 'Loading video...' 
                    : 'Video will close automatically when finished'
              }
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
